import { useState, useEffect } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { Separator } from '../components/ui/separator';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { getSaveaPhone, getSaveaPhoneTel, getSpecialistMessage } from '../config/phoneConfig';
import { 
  CheckSquare, 
  Plus, 
  ExternalLink, 
  Calendar,
  Phone,
  CreditCard,
  Zap,
  Wifi,
  FileText,
  Home,
  Shield,
  Trash2,
  Car,
  Briefcase,
  Smartphone,
  Clock
} from 'lucide-react';

export const MovingChecklistPage = () => {
  const { t, i18n } = useTranslation();
  const [checkedItems, setCheckedItems] = useState(new Set());
  const [customTasks, setCustomTasks] = useState([]);
  const [newTaskTitle, setNewTaskTitle] = useState('');

  const isSpanish = i18n.language === 'es';
  const country = isSpanish ? 'ES' : 'FR';
  const electricitySpecialist = getSpecialistMessage(country, 'electricity');
  const internetSpecialist = getSpecialistMessage(country, 'internet');

  const defaultTasks = [
    // ÉNERGIE
    {
      id: 'energy-contract',
      category: isSpanish ? 'Energía' : 'Énergie',
      icon: Zap,
      title: isSpanish ? 'Cambiar contrato electricidad' : 'Changer contrat électricité',
      description: isSpanish ? 'Contactar nuevo proveedor 2 semanas antes' : 'Contacter nouveau fournisseur 2 semaines avant',
      urgency: 'high',
      timeframe: isSpanish ? '2 semanas antes' : '2 semaines avant',
      savea_help: true
    },
    {
      id: 'gas-contract',
      category: isSpanish ? 'Energía' : 'Énergie', 
      icon: Zap,
      title: isSpanish ? 'Cambiar contrato gas' : 'Changer contrat gaz',
      description: isSpanish ? 'Sincronizar con electricidad' : 'Synchroniser avec électricité',
      urgency: 'high',
      timeframe: isSpanish ? '2 semanas antes' : '2 semaines avant',
      savea_help: true
    },

    // INTERNET
    {
      id: 'internet-contract',
      category: 'Internet',
      icon: Wifi,
      title: isSpanish ? 'Contratar internet nueva vivienda' : 'Souscrire internet nouveau logement',
      description: isSpanish ? 'Programar instalación fecha mudanza' : 'Programmer installation date déménagement',
      urgency: 'high',
      timeframe: isSpanish ? '3 semanas antes' : '3 semaines avant',
      savea_help: true
    },

    // ASSURANCES
    {
      id: 'home-insurance',
      category: isSpanish ? 'Seguros' : 'Assurances',
      icon: Shield,
      title: isSpanish ? 'Seguro hogar nueva vivienda' : 'Assurance habitation nouveau logement',
      description: isSpanish ? 'Activar antes de recibir llaves' : 'Activer avant réception clés',
      urgency: 'high',
      timeframe: isSpanish ? '1 semana antes' : '1 semaine avant'
    },

    // LOGEMENT
    {
      id: 'inventory-old',
      category: isSpanish ? 'Vivienda' : 'Logement',
      icon: Home,
      title: isSpanish ? 'Estado de lugares salida' : 'État des lieux sortie',
      description: isSpanish ? 'Programar con propietario' : 'Programmer avec propriétaire',
      urgency: 'high',
      timeframe: isSpanish ? '1 semana antes' : '1 semaine avant'
    },
    {
      id: 'inventory-new',
      category: isSpanish ? 'Vivienda' : 'Logement',
      icon: Home,
      title: isSpanish ? 'Estado de lugares entrada' : 'État des lieux entrée',
      description: isSpanish ? 'Revisar detalladamente' : 'Vérifier minutieusement',
      urgency: 'high',
      timeframe: isSpanish ? 'Día de mudanza' : 'Jour du déménagement'
    },

    // MATÉRIEL
    {
      id: 'buy-boxes',
      category: isSpanish ? 'Material' : 'Matériel',
      icon: FileText,
      title: isSpanish ? 'Comprar cajas mudanza' : 'Acheter cartons déménagement',
      description: isSpanish ? 'Calcular cantidad necesaria' : 'Calculer quantité nécessaire',
      urgency: 'medium',
      timeframe: isSpanish ? '2 semanas antes' : '2 semaines avant'
    },
    {
      id: 'packing-material',
      category: isSpanish ? 'Material' : 'Matériel',
      icon: FileText,
      title: isSpanish ? 'Material embalaje' : 'Matériel emballage',
      description: isSpanish ? 'Papel burbuja, cinta, etiquetas' : 'Papier bulle, adhésif, étiquettes',
      urgency: 'medium',
      timeframe: isSpanish ? '2 semanas antes' : '2 semaines avant'
    },

    // ADMINISTRATIF
    {
      id: 'mail-redirect',
      category: isSpanish ? 'Administrativo' : 'Administratif',
      icon: FileText,
      title: isSpanish ? 'Redirección correo postal' : 'Réexpédition courrier postal',
      description: isSpanish ? 'Solicitar cambio dirección' : 'Demander changement adresse',
      urgency: 'high',
      timeframe: isSpanish ? '1 semana antes' : '1 semaine avant'
    },
    {
      id: 'bank-address',
      category: isSpanish ? 'Administrativo' : 'Administratif',
      icon: CreditCard,
      title: isSpanish ? 'Cambio dirección bancaria' : 'Changement adresse bancaire',
      description: isSpanish ? 'Todas las cuentas y tarjetas' : 'Tous comptes et cartes',
      urgency: 'medium',
      timeframe: isSpanish ? '1 semana después' : '1 semaine après'
    },
    {
      id: 'work-address',
      category: isSpanish ? 'Administrativo' : 'Administratif',
      icon: Briefcase,
      title: isSpanish ? 'Informar cambio empleador' : 'Informer changement employeur',
      description: isSpanish ? 'RH y nóminas' : 'RH et fiches de paie',
      urgency: 'high',
      timeframe: isSpanish ? '2 semanas antes' : '2 semaines avant'
    },
    {
      id: 'city-registration',
      category: isSpanish ? 'Administrativo' : 'Administratif',
      icon: Home,
      title: isSpanish ? 'Empadronamiento ayuntamiento' : 'Inscription mairie',
      description: isSpanish ? 'Registro nueva residencia' : 'Enregistrement nouvelle résidence',
      urgency: 'medium',
      timeframe: isSpanish ? '2 semanas después' : '2 semaines après'
    },

    // TRANSPORT
    {
      id: 'vehicle-registration',
      category: isSpanish ? 'Transporte' : 'Transport',
      icon: Car,
      title: isSpanish ? 'Cambio dirección vehículos' : 'Changement adresse véhicules',
      description: isSpanish ? 'Carta gris y seguro auto' : 'Carte grise et assurance auto',
      urgency: 'medium',
      timeframe: isSpanish ? '1 mes después' : '1 mois après'
    },

    // SERVICES
    {
      id: 'subscriptions-update',
      category: isSpanish ? 'Servicios' : 'Services',
      icon: Smartphone,
      title: isSpanish ? 'Cambiar dirección suscripciones' : 'Changer adresse abonnements',
      description: isSpanish ? 'Netflix, Spotify, Amazon, etc.' : 'Netflix, Spotify, Amazon, etc.',
      urgency: 'low',
      timeframe: isSpanish ? '1 semana después' : '1 semaine après'
    },

    // SANTÉ
    {
      id: 'doctor-transfer',
      category: isSpanish ? 'Salud' : 'Santé',
      icon: FileText,
      title: isSpanish ? 'Cambio médico familia' : 'Changement médecin traitant',
      description: isSpanish ? 'Transferir historial médico' : 'Transférer dossier médical',
      urgency: 'medium',
      timeframe: isSpanish ? '1 mes después' : '1 mois après'
    },

    // ÉDUCATION
    {
      id: 'school-registration',
      category: isSpanish ? 'Educación' : 'Éducation',
      icon: FileText,
      title: isSpanish ? 'Inscripción escolar niños' : 'Inscription scolaire enfants',
      description: isSpanish ? 'Nuevos colegios y guarderías' : 'Nouvelles écoles et crèches',
      urgency: 'high',
      timeframe: isSpanish ? '1 mes antes' : '1 mois avant'
    },

    // DÉMÉNAGEMENT
    {
      id: 'moving-company',
      category: isSpanish ? 'Logística' : 'Logistique',
      icon: Car,
      title: isSpanish ? 'Contratar empresa mudanza' : 'Contracter entreprise déménagement',
      description: isSpanish ? 'Pedir varios presupuestos' : 'Demander plusieurs devis',
      urgency: 'high',
      timeframe: isSpanish ? '4 semanas antes' : '4 semaines avant'
    },
    {
      id: 'cleaning-old',
      category: isSpanish ? 'Logística' : 'Logistique',
      icon: Home,
      title: isSpanish ? 'Limpieza vivienda anterior' : 'Nettoyage ancien logement',
      description: isSpanish ? 'Después de sacar muebles' : 'Après sortie des meubles',
      urgency: 'medium',
      timeframe: isSpanish ? 'Día mudanza' : 'Jour déménagement'
    },
    {
      id: 'cleaning-new',
      category: isSpanish ? 'Logística' : 'Logistique',
      icon: Home,
      title: isSpanish ? 'Limpieza nueva vivienda' : 'Nettoyage nouveau logement',
      description: isSpanish ? 'Antes de colocar muebles' : 'Avant installation des meubles',
      urgency: 'medium',
      timeframe: isSpanish ? 'Día antes mudanza' : 'Veille du déménagement'
    }
  ];

  const allTasks = [...defaultTasks, ...customTasks];

  useEffect(() => {
    // Load saved state from localStorage
    const saved = localStorage.getItem('savea-moving-checklist');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        setCheckedItems(new Set(data.checkedItems || []));
        setCustomTasks(data.customTasks || []);
      } catch (error) {
        console.error('Error loading saved checklist:', error);
      }
    }
  }, []);

  useEffect(() => {
    // Save state to localStorage
    const data = {
      checkedItems: Array.from(checkedItems),
      customTasks
    };
    localStorage.setItem('savea-moving-checklist', JSON.stringify(data));
  }, [checkedItems, customTasks]);

  const handleTaskCheck = (taskId) => {
    const newChecked = new Set(checkedItems);
    if (newChecked.has(taskId)) {
      newChecked.delete(taskId);
    } else {
      newChecked.add(taskId);
      toast.success(isSpanish ? '¡Tarea completada!' : 'Tâche terminée !');
    }
    setCheckedItems(newChecked);
  };

  const addCustomTask = () => {
    if (!newTaskTitle.trim()) return;

    const newTask = {
      id: `custom-${Date.now()}`,
      category: isSpanish ? 'Personalizado' : 'Personnalisé',
      icon: Plus,
      title: newTaskTitle.trim(),
      description: isSpanish ? 'Tarea personalizada' : 'Tâche personnalisée',
      urgency: 'medium',
      timeframe: isSpanish ? 'A definir' : 'À définir',
      isCustom: true
    };

    setCustomTasks([...customTasks, newTask]);
    setNewTaskTitle('');
    toast.success(isSpanish ? 'Tarea añadida' : 'Tâche ajoutée');
  };

  const deleteCustomTask = (taskId) => {
    setCustomTasks(customTasks.filter(task => task.id !== taskId));
    const newChecked = new Set(checkedItems);
    newChecked.delete(taskId);
    setCheckedItems(newChecked);
    toast.success(isSpanish ? 'Tarea eliminada' : 'Tâche supprimée');
  };

  const getProgressPercentage = () => {
    if (allTasks.length === 0) return 0;
    return Math.round((checkedItems.size / allTasks.length) * 100);
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getUrgencyText = (urgency) => {
    if (isSpanish) {
      switch (urgency) {
        case 'high': return 'Urgente';
        case 'medium': return 'Moderado';
        case 'low': return 'Bajo';
        default: return 'Normal';
      }
    } else {
      switch (urgency) {
        case 'high': return 'Urgent';
        case 'medium': return 'Modéré';
        case 'low': return 'Faible';
        default: return 'Normal';
      }
    }
  };

  const groupedTasks = allTasks.reduce((groups, task) => {
    const category = task.category;
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(task);
    return groups;
  }, {});

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 to-white">
      {/* Hero Section */}
      <section className="py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl lg:text-5xl font-bold text-sage-900 mb-4">
              {isSpanish ? 'Lista de Tareas Mudanza' : 'To-Do List Déménagement'}
            </h1>
            <p className="text-xl text-sage-600 max-w-3xl mx-auto mb-8">
              {isSpanish 
                ? 'Lista completa y personalizable para organizar tu mudanza sin estrés. No olvides ningún trámite importante.'
                : 'Checklist complète et personnalisable pour organiser votre déménagement sans stress. N\'oubliez aucune démarche importante.'
              }
            </p>

            {/* Progress Card */}
            <Card className="max-w-md mx-auto mb-8">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center">
                      <CheckSquare className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="font-semibold text-sage-900">
                        {isSpanish ? 'Progreso' : 'Progression'}
                      </div>
                      <div className="text-sm text-sage-600">
                        {checkedItems.size}/{allTasks.length} {isSpanish ? 'tareas' : 'tâches'}
                      </div>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-indigo-600">
                    {getProgressPercentage()}%
                  </div>
                </div>
                <Progress value={getProgressPercentage()} className="h-2" />
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Add Custom Task */}
      <section className="pb-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-indigo-600" />
                {isSpanish ? 'Añadir tarea personalizada' : 'Ajouter une tâche personnalisée'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4">
                <Input
                  placeholder={isSpanish ? 'Título de la tarea...' : 'Titre de la tâche...'}
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                  className="flex-1 border-sage-200 focus:border-sage-500"
                />
                <Button 
                  onClick={addCustomTask}
                  disabled={!newTaskTitle.trim()}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {isSpanish ? 'Añadir' : 'Ajouter'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Tasks by Category */}
      <section className="pb-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {Object.entries(groupedTasks).map(([category, tasks]) => (
              <div key={category}>
                <h2 className="text-2xl font-bold text-sage-900 mb-6 flex items-center gap-2">
                  <div className="w-8 h-8 bg-sage-100 rounded-lg flex items-center justify-center">
                    {tasks[0] && React.createElement(tasks[0].icon, { className: "w-4 h-4 text-sage-600" })}
                  </div>
                  {category}
                </h2>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {tasks.map((task) => {
                    const isChecked = checkedItems.has(task.id);
                    const TaskIcon = task.icon;

                    return (
                      <Card 
                        key={task.id}
                        className={`transition-all duration-300 ${
                          isChecked 
                            ? 'bg-green-50 border-green-200 opacity-75' 
                            : 'hover:shadow-lg hover:-translate-y-1 bg-white border-sage-200'
                        }`}
                      >
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <Checkbox
                              checked={isChecked}
                              onCheckedChange={() => handleTaskCheck(task.id)}
                              className="mt-1"
                            />
                            
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <TaskIcon className="w-5 h-5 text-sage-600" />
                                  <h3 className={`font-semibold ${isChecked ? 'line-through text-sage-500' : 'text-sage-900'}`}>
                                    {task.title}
                                  </h3>
                                </div>
                                
                                <div className="flex items-center gap-2">
                                  <Badge className={getUrgencyColor(task.urgency)}>
                                    {getUrgencyText(task.urgency)}
                                  </Badge>
                                  {task.isCustom && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => deleteCustomTask(task.id)}
                                      className="text-red-500 hover:text-red-700 h-6 w-6 p-0"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </div>

                              <p className={`text-sm mb-3 ${isChecked ? 'text-sage-400' : 'text-sage-600'}`}>
                                {task.description}
                              </p>

                              <div className="flex items-center gap-2 text-sm text-sage-500">
                                <Calendar className="w-4 h-4" />
                                {task.timeframe}
                              </div>

                              {/* CTA SAVEA pour énergie et internet */}
                              {task.savea_help && (
                                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                  <div className="text-xs text-blue-700 mb-2">
                                    {isSpanish ? '💡 Ayuda SAVEA disponible' : '💡 Aide SAVEA disponible'}
                                  </div>
                                  <Button
                                    size="sm"
                                    onClick={() => window.open(`tel:${getSaveaPhoneTel(country, task.category === 'Internet' ? 'internet' : 'electricity')}`)}
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium text-xs"
                                  >
                                    <Phone className="w-3 h-3 mr-2" />
                                    {isSpanish ? 'Llamar Especialista SAVEA' : 'Appeler Spécialiste SAVEA'}
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA SAVEA pour déménagement - Endroit logique */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-indigo-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            {isSpanish ? '¿Necesitas ayuda con tus contratos?' : 'Besoin d\'aide pour vos contrats ?'}
          </h2>
          <p className="text-blue-100 text-lg mb-8">
            {isSpanish 
              ? 'Nuestros especialistas te ayudan gratuitamente con todos tus cambios de proveedores'
              : 'Nos spécialistes vous aident gratuitement avec tous vos changements de fournisseurs'
            }
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            <Card className="bg-white p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-3 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-sage-900 mb-2">{electricitySpecialist.title}</h3>
              <p className="text-sm text-sage-600 mb-4">{electricitySpecialist.description}</p>
              <Button 
                onClick={() => window.open(`tel:${getSaveaPhoneTel(country, 'electricity')}`)}
                className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-semibold"
              >
                <Phone className="w-4 h-4 mr-2" />
                {getSaveaPhone(country, 'electricity')}
              </Button>
            </Card>

            <Card className="bg-white p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-3 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center">
                <Wifi className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-sage-900 mb-2">{internetSpecialist.title}</h3>
              <p className="text-sm text-sage-600 mb-4">{internetSpecialist.description}</p>
              <Button 
                onClick={() => window.open(`tel:${getSaveaPhoneTel(country, 'internet')}`)}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-semibold"
              >
                <Phone className="w-4 h-4 mr-2" />
                {getSaveaPhone(country, 'internet')}
              </Button>
            </Card>
          </div>
          
          <p className="text-blue-100 text-sm mt-6">
            {isSpanish 
              ? '💡 Servicio gratuito - Especialistas SAVEA en negociación con proveedores de energía e internet'
              : '💡 Appel gratuit - Spécialistes SAVEA en négociation avec fournisseurs énergie et internet'
            }
          </p>
        </div>
      </section>
    </div>
  );
};